<script setup>
import { ref, watch, nextTick, onBeforeUnmount, computed } from 'vue'
import { usePage } from '@inertiajs/vue3'
import UiModal from '@/Components/UI/UiModal.vue'
import UiButton from '@/Components/UI/UiButton.vue'
import UiInput from '@/Components/UI/UiInput.vue'

const page = usePage()
const acceptLanguage = computed(() => page.props?.app_setting?.language || 'en')

const props = defineProps({
  open:           { type: Boolean, required: true },
  title:          { type: String,  default: 'Set location' },
  address:        { type: String,  default: '' },
  lat:            { type: [Number, String, null], default: null },
  lng:            { type: [Number, String, null], default: null },
  officeAddress:  { type: String,  default: '' },
  officeLat:      { type: [Number, String, null], default: null },
  officeLng:      { type: [Number, String, null], default: null },
  saving:         { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'save'])

const SAMUI_FALLBACK = [9.5320, 100.0610]

const localAddress = ref('')
const localLat     = ref(null)
const localLng     = ref(null)
const geocoding    = ref(false)

const searchResults = ref([])
const searching     = ref(false)
const showResults   = ref(false)

const mapEl = ref(null)
let map     = null
let pin     = null
let resizeObserver = null
let geocodeAbort = null
let searchAbort  = null
let searchTimer  = null

const hasOffice = computed(() =>
  props.officeLat != null && props.officeLng != null && String(props.officeAddress || '').trim() !== '',
)

function num(v) {
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

function startCenter() {
  if (num(localLat.value) != null && num(localLng.value) != null) {
    return [num(localLat.value), num(localLng.value)]
  }
  if (num(props.officeLat) != null && num(props.officeLng) != null) {
    return [num(props.officeLat), num(props.officeLng)]
  }
  return SAMUI_FALLBACK
}

async function searchAddress(query) {
  if (searchAbort) searchAbort.abort()
  if (!query || query.trim().length < 3) {
    searchResults.value = []
    showResults.value = false
    searching.value = false
    return
  }
  const ctrl = new AbortController()
  searchAbort = ctrl
  searching.value = true
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(query)}&limit=6&addressdetails=1`
    const res = await fetch(url, {
      signal: ctrl.signal,
      headers: { 'Accept-Language': acceptLanguage.value },
    })
    if (!res.ok) return
    const data = await res.json()
    if (Array.isArray(data)) {
      searchResults.value = data
      showResults.value = data.length > 0
    }
  } catch (e) {
    if (e.name !== 'AbortError') {
      // Silent on network/parse errors.
    }
  } finally {
    if (searchAbort === ctrl) {
      searchAbort = null
      searching.value = false
    }
  }
}

function onAddressInput(value) {
  localAddress.value = value
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(() => searchAddress(value), 350)
}

function pickResult(r) {
  const lat = Number(r.lat)
  const lng = Number(r.lon)
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return
  localAddress.value = r.display_name || localAddress.value
  placePin(lat, lng, { geocode: false })
  if (map) map.setView([lat, lng], 15)
  searchResults.value = []
  showResults.value = false
}

async function reverseGeocode(lat, lng) {
  if (geocodeAbort) geocodeAbort.abort()
  const ctrl = new AbortController()
  geocodeAbort = ctrl
  geocoding.value = true
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    const res = await fetch(url, {
      signal: ctrl.signal,
      headers: { 'Accept-Language': acceptLanguage.value },
    })
    if (!res.ok) return
    const data = await res.json()
    if (data && data.display_name) {
      localAddress.value = data.display_name
    }
  } catch (e) {
    if (e.name !== 'AbortError') {
      // Network or parse error — leave the address as-is.
    }
  } finally {
    if (geocodeAbort === ctrl) {
      geocodeAbort = null
      geocoding.value = false
    }
  }
}

function placePin(lat, lng, { geocode = true } = {}) {
  if (!map) return
  const L = window.L
  if (pin) {
    pin.setLatLng([lat, lng])
  } else {
    pin = L.marker([lat, lng], { draggable: true }).addTo(map)
    pin.on('dragend', () => {
      const ll = pin.getLatLng()
      localLat.value = +ll.lat.toFixed(7)
      localLng.value = +ll.lng.toFixed(7)
      reverseGeocode(localLat.value, localLng.value)
    })
  }
  localLat.value = +Number(lat).toFixed(7)
  localLng.value = +Number(lng).toFixed(7)
  if (geocode) reverseGeocode(localLat.value, localLng.value)
}

function clearPin() {
  if (pin && map) {
    map.removeLayer(pin)
    pin = null
  }
}

async function initMap() {
  if (map || !mapEl.value) return
  const L = (await import('leaflet')).default
  await import('leaflet/dist/leaflet.css')
  window.L = L

  // Leaflet's default marker icons break under bundlers — point at the CDN.
  delete L.Icon.Default.prototype._getIconUrl
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl:       'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl:     'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  })

  map = L.map(mapEl.value).setView(startCenter(), 14)
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  }).addTo(map)

  map.on('click', (e) => {
    placePin(e.latlng.lat, e.latlng.lng)
  })

  if (num(localLat.value) != null && num(localLng.value) != null) {
    placePin(num(localLat.value), num(localLng.value), { geocode: false })
  }

  resizeObserver = new ResizeObserver(() => map && map.invalidateSize())
  resizeObserver.observe(mapEl.value)
}

function destroyMap() {
  if (geocodeAbort) { geocodeAbort.abort(); geocodeAbort = null }
  if (searchAbort)  { searchAbort.abort();  searchAbort  = null }
  if (searchTimer)  { clearTimeout(searchTimer); searchTimer = null }
  geocoding.value = false
  searching.value = false
  searchResults.value = []
  showResults.value = false
  if (resizeObserver) { resizeObserver.disconnect(); resizeObserver = null }
  if (pin) pin = null
  if (map) { map.remove(); map = null }
}

function syncFromProps() {
  localAddress.value = props.address || ''
  localLat.value     = num(props.lat)
  localLng.value     = num(props.lng)
}

function useOffice() {
  if (!hasOffice.value) return
  localAddress.value = props.officeAddress
  const lat = num(props.officeLat)
  const lng = num(props.officeLng)
  if (lat != null && lng != null) {
    placePin(lat, lng, { geocode: false })
    if (map) map.setView([lat, lng], 15)
  }
}

function clearAll() {
  localAddress.value = ''
  localLat.value = null
  localLng.value = null
  searchResults.value = []
  showResults.value = false
  clearPin()
}

function onSave() {
  emit('save', {
    address: localAddress.value.trim(),
    lat:     localLat.value,
    lng:     localLng.value,
  })
}

watch(() => props.open, async (v) => {
  if (v) {
    syncFromProps()
    await nextTick()
    await initMap()
    // Re-center after the modal animation finishes so Leaflet measures correctly.
    setTimeout(() => map && map.invalidateSize(), 220)
  } else {
    destroyMap()
  }
}, { immediate: false })

onBeforeUnmount(destroyMap)
</script>

<template>
  <UiModal
    :open="open"
    :title="title"
    size="lg"
    @close="emit('close')"
  >
    <div class="space-y-4">
      <div>
        <div class="flex items-center justify-between mb-1">
          <label class="block text-sm font-medium text-[color:var(--inkSoft)]">
            Address
          </label>
          <span
            v-if="geocoding || searching"
            class="text-xs text-[color:var(--muted)]"
          >{{ searching ? 'Searching…' : 'Looking up address…' }}</span>
        </div>
        <div class="relative">
          <UiInput
            :model-value="localAddress"
            placeholder="Type a place (e.g. Phuket, Lamai Beach) or click the map"
            @update:model-value="onAddressInput"
          />
          <ul
            v-if="showResults"
            class="absolute z-10 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-[var(--radius-md)] border border-[color:var(--line)] bg-[color:var(--surface)] shadow-[var(--shadow-md)]"
          >
            <li
              v-for="r in searchResults"
              :key="r.place_id"
              class="px-3 py-2 text-sm cursor-pointer hover:bg-[color:var(--surfaceAlt)] text-[color:var(--ink)] border-b border-[color:var(--line)] last:border-b-0"
              @click="pickResult(r)"
            >
              {{ r.display_name }}
            </li>
          </ul>
        </div>
        <p class="mt-1 text-xs text-[color:var(--muted)]">
          Type a place to search, or click the map to drop a pin — both fill this field. Edit freely; what you type here is what appears on the contract.
        </p>
      </div>

      <div>
        <div class="flex items-center justify-between mb-1">
          <label class="block text-sm font-medium text-[color:var(--inkSoft)]">
            Pin on map
          </label>
          <span
            v-if="localLat != null && localLng != null"
            class="text-xs tabular-nums text-[color:var(--muted)]"
          >
            {{ Number(localLat).toFixed(5) }}, {{ Number(localLng).toFixed(5) }}
          </span>
        </div>
        <div
          ref="mapEl"
          class="w-full h-[320px] md:h-[400px] overflow-hidden"
          :style="{ border: '1px solid var(--line)', borderRadius: 'var(--radius-table)' }"
        />
        <p class="mt-1 text-xs text-[color:var(--muted)]">
          Click anywhere on the map to drop a pin, then drag to adjust. The address above updates automatically.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2 pt-1">
        <UiButton
          v-if="hasOffice"
          type="button"
          variant="secondary"
          size="sm"
          @click="useOffice"
        >
          Use office location
        </UiButton>
        <UiButton
          type="button"
          variant="ghost"
          size="sm"
          @click="clearAll"
        >
          Clear
        </UiButton>
      </div>
    </div>

    <template #footer>
      <UiButton
        type="button"
        variant="ghost"
        @click="emit('close')"
      >
        Cancel
      </UiButton>
      <UiButton
        type="button"
        variant="primary"
        :loading="saving"
        @click="onSave"
      >
        {{ saving ? 'Saving…' : 'Save' }}
      </UiButton>
    </template>
  </UiModal>
</template>
